import React, {useEffect} from "react";
import "./styles/OverviewPage.scss"
import fileDownload from "js-file-download";
import {Radio} from "@atlaskit/radio";
import TagGroup from "@atlaskit/tag-group";
import Tooltip from "@atlaskit/tooltip";
import CheckCircleIcon from "@atlaskit/icon/glyph/check-circle";
import CrossCircleIcon from "@atlaskit/icon/glyph/cross-circle";
import Button, {IconButton} from "@atlaskit/button/new";
import SettingsIcon from "@atlaskit/icon/glyph/settings";
import EditFilledIcon from "@atlaskit/icon/glyph/edit-filled";
import DropdownMenu, {DropdownItem, DropdownItemGroup} from "@atlaskit/dropdown-menu";
import MoreIcon from "@atlaskit/icon/glyph/more";
import {ModalTransition} from "@atlaskit/modal-dialog";
import {ConfirmDialog} from "./ConfirmDialog";
import {Menu} from "navigation-common/types/navigation-types";
import {SelectOption} from "navigation-common/types/html-types";
import {PAGE_ADMIN_SINGLE_MENU_CONFIG, PAGE_GENERAL_SINGLE_MENU_CONFIG, PARAM_APP_MENU_CONFIG_MENU_KEY} from "navigation-common/types/pages-constants";
import {SimpleTag} from "@atlaskit/tag";
import {handleLocationBasedInAppLink} from "../../common/in-app-goto-handler";
import {navigationService} from "../../api/NavigationService";
import {useAsyncFn} from "react-use";
import {GroupOption} from "./AsyncGroupSelect";
import {confluenceService} from "../../api/ConfluenceService";
import {errorFlag} from "../../model/flags";
import {useFlags} from "@atlaskit/flag";

export const OverviewMenuEntry = ({menu, changeDefaultMenu, copyMenu, onDeleteMenu, onEditMenuMetaData, groupIds, allSpaceLabels}: {
    menu: Menu,
    changeDefaultMenu: (menu: Menu) => void,
    copyMenu: (menu: Menu) => void,
    onDeleteMenu: (menu: Menu) => Promise<void>,
    onEditMenuMetaData: (menu: Menu) => void,
    groupIds: Array<string>,
    allSpaceLabels: Array<SelectOption>
}) => {

    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [isOpenEditMenu, setIsOpenEditMenu] = React.useState(false);
    const [selectedGroups, setSelectedGroups] = React.useState<Array<GroupOption>>([]);
    const {showFlag} = useFlags();

    const [resolveGroupsState, resolveGroups] = useAsyncFn(async () => {
        const initGroups: GroupOption[] = [];

        try {
            for (const groupId of groupIds) {
                const group = await confluenceService.getGroupById(groupId);
                initGroups.push({value: group.id, label: group.name});
            }
        } catch (e) {
            handleOperationError("There was an error while fetching groups. Please try again later or contact support.");
        }

        setSelectedGroups(initGroups);
    }, [groupIds]);

    useEffect(() => {
        resolveGroups();
    }, [groupIds]);

    const handleOperationError = (error?: string) => {
        const errMsg = error ?? "There was an error. Please try again later or contact support.";
        showFlag(errorFlag({title: "Error!", description: errMsg}));
    }

    const exportMenu = async (menu: Menu) => {
        menu.anonymousAccess = false;
        menu.default = false;
        const cleanedMenu = navigationService.removeImageStorageNameFromMenu(menu);
        const menuJsonData = JSON.stringify(cleanedMenu);
        const fileName = cleanedMenu.name.replaceAll(/\W/g, "_").toLowerCase();
        fileDownload(menuJsonData, fileName + ".json");
    }

    return (<>
            <div className={"menu-list-group"} key={menu.key} onClick={() => {
                onEditMenuMetaData(menu)
            }}>
                <div className={"menu-list-item default"}>
                    <Radio
                        name="radio-default"
                        testId="radio-default"
                        isChecked={menu.default}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        onChange={(e) => {
                            e.stopPropagation();
                            changeDefaultMenu(menu)
                        }} style={{cursor: "pointer"}}
                    />
                </div>
                <div className={"menu-list-item name"}>
                    {menu.name}
                </div>

                <div className={"menu-list-item groups"}>
                    <TagGroup>
                        {menu.anonymousAccess && (
                            <span className="anonymous-tag">
                            <SimpleTag text={"anonymous-users"}/>
                        </span>
                        )}
                        {menu.userGroups && selectedGroups?.map((group) => {
                            return <Tooltip position={"auto"} key={group.value} content={group.label}>
                                <span className="group-tag">
                                    <SimpleTag text={group.label} color={"standard"}/>
                                </span>
                            </Tooltip>
                        })}
                    </TagGroup>
                </div>

                <div className={"menu-list-item space-labels"}>
                    <TagGroup>
                        {menu.spaceLabels && allSpaceLabels.filter(spaceLabel => menu.spaceLabels?.includes(spaceLabel.value)).map((spaceLabel) => {
                            return <Tooltip position={"auto"} key={spaceLabel.value} content={spaceLabel.label}>
                                <span className="space-labels-tag">
                                    <SimpleTag text={spaceLabel.label}/>
                                </span>
                            </Tooltip>
                        })}
                    </TagGroup>
                </div>

                <div className={"menu-list-item color"} style={{gap: "16px", top: "3px", position: "relative"}}>
                    <div>
                        <div className={"header"}>Light</div>
                        <div className={"color-picker-swatch"} style={{display: "flex", gap: "6px"}}>
                            <Tooltip content={"Top level: " + menu.primaryColor}>
                                <div className={"color-picker-preview"} style={{backgroundColor: menu.primaryColor}}/>
                            </Tooltip>
                            <Tooltip content={"Sub level: " + menu.subMenuBgColor}>
                                <div className={"color-picker-preview"} style={{backgroundColor: menu.subMenuBgColor}}/>
                            </Tooltip>
                        </div>
                    </div>
                    <div>
                        <div className={"header"}>Dark</div>
                        <div className={"color-picker-swatch"} style={{display: "flex", gap: "6px"}}>
                            <Tooltip content={"Top level: " + menu.primaryColorDarkTheme}>
                                <div className={"color-picker-preview"} style={{backgroundColor: menu.primaryColorDarkTheme}}/>
                            </Tooltip>
                            <Tooltip content={"Sub level: " + menu.subMenuBgColorDarkTheme}>
                                <div className={"color-picker-preview"} style={{backgroundColor: menu.subMenuBgColorDarkTheme}}/>
                            </Tooltip>
                        </div>
                    </div>
                </div>

                <div className={"menu-list-item visibility"}>
                    <Tooltip content={menu.active ? "Menu is visible" : "Menu is not visible"}>
                        {menu.active
                            ? <span className={"menu-active"}><CheckCircleIcon label={"menu is visible"}/></span>
                            : <span className={"menu-inactive"}><CrossCircleIcon label={"menu is invisible"}/></span>}
                    </Tooltip>
                </div>

                <span className={"menu-list-item actions"}>
                    <Tooltip content="Edit menu configuration">
                         <IconButton label={"Edit menu configuration"} onClick={(e) => {
                             e.stopPropagation();
                             onEditMenuMetaData(menu)
                         }} appearance={"default"} icon={(iconProps) => (
                             <SettingsIcon {...iconProps} size="small"/>
                         )}/>
                    </Tooltip>
                    <div className={"button structure"}>
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsOpenEditMenu(true);
                                handleLocationBasedInAppLink(PAGE_ADMIN_SINGLE_MENU_CONFIG.key, PAGE_GENERAL_SINGLE_MENU_CONFIG.key, {[PARAM_APP_MENU_CONFIG_MENU_KEY]: menu.key})
                            }}
                            appearance={"default"}
                            iconBefore={(iconProps) => (
                                <EditFilledIcon {...iconProps} size="small"/>
                            )}
                            isLoading={isOpenEditMenu}>Edit menu structure</Button>
                    </div>
                    <div onClick={(e) => {
                        e.stopPropagation();
                    }}>
                    <DropdownMenu<HTMLButtonElement> placement={"bottom-end"}
                                                     trigger={({triggerRef, ...triggerProps}) => (
                                                         <IconButton {...triggerProps}
                                                                     ref={triggerRef}
                                                                     spacing={"default"}
                                                                     icon={(iconProps) => (
                                                                         <MoreIcon {...iconProps} size="small"/>
                                                                     )}
                                                                     label={"more actions"}
                                                         />
                                                     )}
                    >
                      <DropdownItemGroup>
                        <DropdownItem onClick={() => copyMenu(menu)}>Duplicate menu</DropdownItem>
                        <DropdownItem onClick={() => exportMenu(menu)}>Export menu</DropdownItem>
                        <DropdownItem onClick={() => setShowDeleteConfirm(true)}>Delete menu</DropdownItem>
                      </DropdownItemGroup>
                    </DropdownMenu></div>
                </span>
            </div>
            {showDeleteConfirm &&
                <ModalTransition>
                    <ConfirmDialog appearance="danger" key={menu.key}
                                   confirmTitle={menu.default ? `Delete DEFAULT menu: "${menu.name}"` : `Delete menu: "${menu.name}"`}
                                   confirmMsg={menu.default ? "You are about to delete the default menu. This action cannot be undone. Remember to set a new default menu as soon as you are ready." : "Are you sure you want to delete this menu? This action cannot be undone."}
                                   confirmBtnText={"Delete"} cancelBtnText={"Cancel"} onConfirm={() => onDeleteMenu(menu)}
                                   onCancel={() => setShowDeleteConfirm(false)}/>
                </ModalTransition>}
        </>
    );
};


